import { FieldPath, FieldValues, RegisterOptions, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "./hook-form-helper";
import { THookFormFieldProps } from "./hook-form-types";
import { useEffect } from "react";
import RadioButtonGroup, { TRadioButtonGroupProps } from "../radio-button-group/radio-button-group";

export type THookFormRadioButtonGroupRules<T extends FieldValues = FieldValues> = Pick<
  RegisterOptions<T, FieldPath<T>>,
  "required"
>;

type THookFormRadioButtonGroupProps<TFieldValues extends FieldValues> = THookFormFieldProps<TFieldValues> & {
  rules?: THookFormRadioButtonGroupRules<TFieldValues>;
} & Pick<TRadioButtonGroupProps, "radioButtons" | "helperText" | "isDisabled" | "label" | "orientation">;

export const HookFormRadioButtonGroup = <T extends FieldValues>({
  rules,
  control,
  name,
  label,
  helperText,
  radioButtons,
  orientation,
  isDisabled,
  defaultValue,
}: THookFormRadioButtonGroupProps<T>) => {
  const { t } = useTranslation();

  const { field, fieldState } = useController({
    name: name,
    control: control,
    defaultValue: defaultValue,
    disabled: isDisabled,
    rules: rules,
    shouldUnregister: true,
  });

  useEffect(() => {
    if (defaultValue) {
      field.onChange(defaultValue);
    }
  }, []);

  const { error } = fieldState;
  const { value } = field;

  return (
    <RadioButtonGroup
      name={name}
      value={value}
      radioButtons={radioButtons.map((radioButton) => {
        return {
          id: radioButton.id,
          value: radioButton.value,
          isDisabled: radioButton.isDisabled,
          label: radioButton.label,
        };
      })}
      onChangeNative={field.onChange}
      helperText={helperText}
      errorText={getErrorMessage(t, error)}
      label={label}
      isDisabled={isDisabled}
      orientation={orientation}
    />
  );
};
